import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { selectFeatureFlag } from "../../../../features/featureFlags/selectors"
import { REQUEST_ERRORS_SMS } from "../../../../constants/requestErrorFlags"
import { getCreditCard } from "../../../../features/payment/paymentSlice"
import { getLeadDetails, editNewLeadDetails } from "../../../../features/contacts/contacts"
import { setTemplate, signTemplate, getSignDocuments } from "../../../../features/documents/documents"
import AddMethodPaymentModal from "../../../../components/PaymentMethodTab/AddPaymentMethodModal"
import { showSnackBar } from "../../../../components/Snackbar/Snackbar"
import {
  MAX_CARDS, CLIENT, OTHER, resizeRows, splitTotal, rowProblem, duplicateCard, canSaveSplit, toCardSplit, rowsFromSplit,
} from "./cardSplitRules"

// The card split (WS #2228900, under #2219705). PACK collects its one-time flat fee on its own
// platform and may split it across up to 6 cards, some of them other people's. The CRM never charges
// these cards; the split feeds the agreement's card authorizations. Saved on the lead with the
// Program Plan save (PUT /lead-details), which checks it again and refuses with the reason.
//
// Cards are added with the plain card endpoint (isLink false, removeOld false). The Payment Method
// button this panel replaces posts /cards-link with removeOld, which deletes every other billing
// account of the client, and with it the cards of the split.

const money = (amount) => `$${Number(amount || 0).toLocaleString('en-US')}`
const isFulfilled = (result) => result?.meta?.requestStatus === 'fulfilled'
const cardLabel = (card) => `${card.provider || 'Card'} •••• ${card.cardNumberLast4} (${card.nameOnCard || 'no name'})`

const fieldClass = "border border-divider rounded-lg px-3 py-2 text-sm bg-surface text-text-primary"

// WS #2228906. Someone else's card gets its own Card Payment Authorization, signed by the card holder.
// The server takes the holder's email or mobile from the SAVED split, so a card's form is offered only
// while the split on screen is the saved one.
const CARD_AUTH_DOC = 'cardAuthorization'
const splitKey = (rows) => JSON.stringify(toCardSplit(rows))

// The furthest any of the card's forms got: signed beats sent. A signed form whose typed name did not
// match the name on the card (cardAuth.nameMatches === false, recorded by the server) reads as
// 'signedNameMismatch': the signature stands, the rep is told.
const SIGNED_NAME_MISMATCH = 'signedNameMismatch'
const isSigned = (status) => status === 'signed' || status === SIGNED_NAME_MISMATCH
const docStatus = (doc) => {
  if (doc.status !== 'signed') return 'pending'
  return doc.cardAuth?.nameMatches === false ? SIGNED_NAME_MISMATCH : 'signed'
}
const authStatusByCard = (docs) => docs
  .filter((doc) => doc?.typeDoc === CARD_AUTH_DOC && doc?.cardAuth?.billingAccount)
  .reduce((byCard, doc) => {
    const card = String(doc.cardAuth.billingAccount)
    return { ...byCard, [card]: isSigned(byCard[card]) ? byCard[card] : docStatus(doc) }
  }, {})

const CardSplit = ({ userId, flatFee, userData, acceptedPaymentMethods, onSaved }) => {
  const dispatch = useDispatch()
  const smsStrict = useSelector(selectFeatureFlag(REQUEST_ERRORS_SMS))
  const [cards, setCards] = useState([])
  const [rows, setRows] = useState(rowsFromSplit())
  const [saving, setSaving] = useState(false)
  const [savedKey, setSavedKey] = useState(null)
  const [authStatus, setAuthStatus] = useState({})
  const [sendingCard, setSendingCard] = useState(null)
  const cardsRef = useRef([])

  const loadAuthStatus = async () => {
    const result = await dispatch(getSignDocuments({ userId }))
    // The API answers { data: { docs: [...] } }.
    const data = isFulfilled(result) ? result.payload?.data : null
    const docs = Array.isArray(data?.docs) ? data.docs : (Array.isArray(data) ? data : [])
    setAuthStatus(authStatusByCard(docs))
  }

  const loadCards = async () => {
    const result = await dispatch(getCreditCard(userId))
    const list = isFulfilled(result) && Array.isArray(result.payload?.data) ? result.payload.data : []
    cardsRef.current = list
    setCards(list)
    return list
  }

  useEffect(() => {
    if (!userId) return
    loadCards()
    const loadStoredSplit = async () => {
      const result = await dispatch(getLeadDetails(userId))
      const stored = isFulfilled(result) ? result.payload?.data?.programPlan?.cardSplit : null
      if (Array.isArray(stored) && stored.length) {
        const loaded = rowsFromSplit(stored)
        setRows(loaded)
        setSavedKey(splitKey(loaded))
      }
    }
    loadStoredSplit()
    loadAuthStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const updateRow = (index, fields) => setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...fields } : row)))

  // The card endpoint answers with no id, so the new card is the one the list did not have before.
  const onCardSaved = async () => {
    const before = new Set(cardsRef.current.map((card) => String(card._id)))
    const list = await loadCards()
    const added = list.find((card) => !before.has(String(card._id)))
    if (!added) return
    setRows((prev) => {
      const index = prev.findIndex((row) => !row.billingAccount)
      return index === -1 ? prev : prev.map((row, i) => (i === index ? { ...row, billingAccount: String(added._id) } : row))
    })
  }

  const save = async () => {
    setSaving(true)
    try {
      const result = await dispatch(editNewLeadDetails({ userId, data: { programPlan: { cardSplit: toCardSplit(rows) } } }))
      if (isFulfilled(result)) {
        setSavedKey(splitKey(rows))
        showSnackBar("success", "Card split saved")
        onSaved && onSaved()
      } else {
        showSnackBar("error", typeof result?.payload === 'string' ? result.payload : "Failed to save the card split")
      }
    } finally {
      setSaving(false)
    }
  }

  const sendAuthorization = async (billingAccountId) => {
    setSendingCard(billingAccountId)
    try {
      const rendered = await dispatch(setTemplate({ typeDoc: CARD_AUTH_DOC, data: { userId, billingAccountId } }))
      if (!isFulfilled(rendered)) {
        showSnackBar("error", typeof rendered?.payload === 'string' ? rendered.payload : "Failed to prepare the card authorization")
        return
      }
      const sent = await dispatch(signTemplate({ userId, pdf: rendered.payload?.data, typeDoc: CARD_AUTH_DOC, billingAccountId }))
      if (!isFulfilled(sent)) {
        showSnackBar("error", typeof sent?.payload === 'string' ? sent.payload : "Failed to send the card authorization")
        return
      }
      if (smsStrict && sent?.payload?.smsError) {
        showSnackBar("error", `The card authorization was not sent: ${sent.payload.smsError}`)
      } else {
        showSnackBar("success", "Card authorization sent to the card holder")
      }
      await loadAuthStatus()
    } finally {
      setSendingCard(null)
    }
  }

  const isSaved = savedKey !== null && splitKey(rows) === savedKey
  const total = splitTotal(rows)
  const difference = (flatFee || 0) - total

  return (
    <div className="flex flex-col gap-4 py-6 px-12 border-t border-divider">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-bold text-lg">Split the fee across cards</div>
          <div className="text-sm text-text-secondary">One-time fee: {money(flatFee)}. Each card gets its own payment authorization.</div>
        </div>
        <AddMethodPaymentModal
          userId={userId}
          userData={userData}
          isLink={false}
          removeOld={false}
          onCardSaved={onCardSaved}
          acceptedPaymentMethods={acceptedPaymentMethods}
          customButton="Add a card"
          customClasses="w-[140px] h-[38px] bg-primary-main text-white font-bold cursor-pointer text-sm hover:bg-primary-main/90 rounded-lg text-center"
        />
      </div>

      <label className="flex items-center gap-3 text-sm">
        <span>How many cards?</span>
        <select
          aria-label="How many cards?"
          className={fieldClass}
          value={rows.length}
          onChange={(e) => setRows((prev) => resizeRows(prev, e.target.value))}
        >
          {Array.from({ length: MAX_CARDS }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>

      {rows.map((row, index) => {
        const n = index + 1
        const problem = rowProblem(row)
        const touched = row.billingAccount || row.amountText
        return (
          <div key={index} className="flex flex-col gap-2 p-4 rounded-xl border border-divider">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-semibold w-[56px]">Card {n}</span>
              <select
                aria-label={`Card ${n}`}
                className={`${fieldClass} min-w-[260px]`}
                value={row.billingAccount}
                onChange={(e) => updateRow(index, { billingAccount: e.target.value })}
              >
                <option value="">Pick a saved card</option>
                {cards.map((card) => <option key={card._id} value={String(card._id)}>{cardLabel(card)}</option>)}
              </select>
              <input
                aria-label={`Amount for card ${n}`}
                className={`${fieldClass} w-[140px]`}
                placeholder="Amount, $"
                value={row.amountText}
                onChange={(e) => updateRow(index, { amountText: e.target.value })}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  aria-label={`Card ${n} belongs to someone else`}
                  checked={row.holder === OTHER}
                  onChange={(e) => updateRow(index, { holder: e.target.checked ? OTHER : CLIENT })}
                />
                Someone else's card
              </label>
            </div>
            {row.holder === OTHER && (
              <div className="flex flex-wrap items-center gap-3 pl-[68px]">
                <input
                  aria-label={`Cardholder email for card ${n}`}
                  className={`${fieldClass} min-w-[240px]`}
                  placeholder="Cardholder email"
                  value={row.holderEmail}
                  onChange={(e) => updateRow(index, { holderEmail: e.target.value })}
                />
                <input
                  aria-label={`Cardholder mobile for card ${n}`}
                  className={`${fieldClass} w-[180px]`}
                  placeholder="or mobile"
                  value={row.holderPhone}
                  onChange={(e) => updateRow(index, { holderPhone: e.target.value })}
                />
                <span className="text-xs text-text-secondary">They get their own authorization to sign.</span>
              </div>
            )}
            {touched && problem && <div className="text-warning-main text-sm pl-[68px]">{problem}</div>}
            {row.holder === OTHER && (
              <div className="flex items-center gap-3 pl-[68px] text-sm">
                {!isSaved && <span className="text-text-secondary">Save the split to send this card holder their authorization.</span>}
                {isSaved && authStatus[row.billingAccount] === 'signed' && <span className="text-success-main">Signed by the card holder</span>}
                {isSaved && authStatus[row.billingAccount] === SIGNED_NAME_MISMATCH && (
                  <span className="text-warning-main">Signed — the typed name doesn't match the name on the card</span>
                )}
                {isSaved && !isSigned(authStatus[row.billingAccount]) && (
                  <>
                    <span className="text-text-secondary">
                      {authStatus[row.billingAccount] === 'pending' ? 'Sent, waiting for the card holder' : 'Authorization not sent yet'}
                    </span>
                    <button
                      type="button"
                      aria-label={`Send authorization for card ${n}`}
                      className="px-3 h-[32px] border border-primary-main text-primary-main font-semibold text-sm rounded-lg disabled:opacity-50"
                      disabled={sendingCard !== null}
                      onClick={() => sendAuthorization(row.billingAccount)}
                    >
                      {authStatus[row.billingAccount] === 'pending' ? 'Send again' : 'Send authorization'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )
      })}

      {duplicateCard(rows) && <div className="text-warning-main text-sm">Each card can be used only once</div>}

      <div className="flex items-center justify-between">
        <div className="text-sm">
          <span className="font-semibold">Total {money(total)} of {money(flatFee)}</span>
          {difference > 0 && <span className="text-warning-main ml-2">{money(difference)} left</span>}
          {difference < 0 && <span className="text-warning-main ml-2">{money(-difference)} over</span>}
        </div>
        <button
          type="button"
          className="w-[140px] h-[38px] bg-primary-main text-white font-bold text-sm rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={saving || !canSaveSplit({ rows, flatFee })}
          onClick={save}
        >
          Save split
        </button>
      </div>
    </div>
  )
}

export default CardSplit
